import React, { useEffect, useMemo, useRef, useState } from "react";

import Box from "@mui/material/Box";

import { RECORDING_STATE_IN_PROGRESS } from "../medical-scribe-constants";

interface WaveVolumeIndicatorProps {
  volume: number; // Current volume percentage (0-100)
  historyLength?: number; // Number of rectangles to display
  maxHeight?: number; // Maximum height of the rectangles in pixels
  width?: string | number;
  backgroundColor?: string;
  barColor?: string;
  barWidth?: string | number;
  isMicActive?: boolean;
  recordingState: number;
  border?: string;
}

const containerBaseSx = {
  display: "flex",
  alignItems: "flex-end",
  overflow: "hidden",
  borderRadius: "4px"
} as const;

const barBaseSx = {
  width: "2px",
  marginRight: "1px"
} as const;

export default function WaveVolumeIndicator({
  volume,
  historyLength = 50,
  maxHeight = 34,
  width = "115px",
  backgroundColor = "hsla(273, 21%, 25%, 1)",
  barColor = "white",
  barWidth = "2px",
  border = "solid 1px #987FAE",
  isMicActive = false,
  recordingState
}: WaveVolumeIndicatorProps) {
  const recordingEnabled = recordingState === RECORDING_STATE_IN_PROGRESS;
  const [volumeHistory, setVolumeHistory] = useState<number[]>([]);
  const previousRecordingEnabled = useRef(recordingEnabled);
  // Throttle bar pushes so scroll speed is readable (~12fps) rather than 60fps
  const lastPushTimeRef = useRef(0);

  // Clear history when recording stops
  useEffect(() => {
    if (previousRecordingEnabled.current && !recordingEnabled) {
      setVolumeHistory([]);
      lastPushTimeRef.current = 0;
    }
    previousRecordingEnabled.current = recordingEnabled;
  }, [recordingEnabled]);

  // Push a new bar at most every 80ms so existing bars persist long enough to be seen
  useEffect(() => {
    if (!recordingEnabled) return;

    const now = Date.now();
    if (now - lastPushTimeRef.current < 80) return;
    lastPushTimeRef.current = now;

    setVolumeHistory((prev) => {
      if (prev.length >= historyLength) {
        return [...prev.slice(1), volume];
      }
      return [...prev, volume];
    });
  }, [volume, historyLength, recordingEnabled]);

  // Each bar reflects its own stored volume — no global isMicActive override.
  // Silence bars naturally scroll in short as speech bars scroll out.
  const bars = useMemo(() => {
    return volumeHistory.map((vol, index) => {
      const height = Math.max(3, (vol / 100) * maxHeight);
      return (
        <Box
          key={`bar-${index}-${volumeHistory.length}`}
          sx={{
            ...barBaseSx,
            width: barWidth,
            border,
            backgroundColor: barColor,
            height: `${height}px`
          }}
        />
      );
    });
  }, [volumeHistory, maxHeight, barColor, barWidth, border]);

  return (
    <Box
      sx={{
        ...containerBaseSx,
        backgroundColor,
        height: `${maxHeight}px`,
        width,
        // Subtle dim when mic is inactive so the container gives feedback without a jarring wipe
        opacity: isMicActive ? 1 : 0.5,
        transition: "opacity 0.4s ease"
      }}
    >
      {bars}
    </Box>
  );
}
